import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaPlus } from 'react-icons/fa';
import { Button, InputGroup, Form } from 'react-bootstrap';

import Wrapper from '../../components/Wrapper';
import SupportPagination from '../../components/utils/supportPagination';
import PartnerPagination from '../../components/utils/partnerPagination';
import BookingTable from '../../components/tables/BookingTable';
import {
  fetchMyBookings,
  resetBookingsState,
  setCurrentPage
} from '../../store/features/user/BookingsSlice';
import { hasPermission } from '../../hooks/usePermission';
import { getRole } from '../../utils/role';

const SUPPORT_LIMIT = 15;

const Bookings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const role = getRole();
  const isClientSide = (role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'FIELD_OFFICER' || role === 'FARMER';

  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');

  // Client-side pagination state
  const [clientPage, setClientPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const { allBookings, bookings, currentPage, totalPages, status } = useSelector((state) => state.userBookings);

  // --- Data fetch ---
  const loadData = () => {
    if (isClientSide) {
      dispatch(fetchMyBookings());
    } else {
      dispatch(
        fetchMyBookings({
          page: currentPage,
          limit: SUPPORT_LIMIT,
          keyword: submittedSearch || undefined,
        })
      );
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, [dispatch, currentPage, submittedSearch]);

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => {
      dispatch(resetBookingsState());
    };
  }, [dispatch]);

  // --- Search handler ---
  const handleSearch = () => {
    if (isClientSide) return;

    setSubmittedSearch(searchTerm.trim());

    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
  };

  const handleUpdateClick = (bookingData) => {
    navigate(`/bookings/edit/${bookingData.booking_id}`);
  };

  // --- Client-side filtering ---
  const filteredData =
    isClientSide && Array.isArray(allBookings)
      ? allBookings.filter((item) =>
          item &&
          Object.values(item).some(
            (value) =>
              value != null &&
              (typeof value === 'string' || typeof value === 'number') &&
              value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
      : [];

  // --- Pagination logic ---
  let tableData;
  let startIndex;
  let totalClientPages;

  if (isClientSide) {
    totalClientPages = Math.ceil(filteredData.length / itemsPerPage);
    const indexOfFirst = (clientPage - 1) * itemsPerPage;
    tableData = filteredData.slice(indexOfFirst, indexOfFirst + itemsPerPage);
    startIndex = indexOfFirst;
  } else {
    tableData = bookings;
    startIndex = (currentPage - 1) * SUPPORT_LIMIT;
  }

  return (
    <Wrapper>
      <div className='reports-container py-4'>
        <div className='d-flex flex-column flex-md-row justify-content-between mb-3 w-100'>
          <h1 className='fw-bolder'>Bookings</h1>

          <div className='d-flex justify-content-end align-items-center mt-3 m-md-0 gap-2'>
            <InputGroup>
              <Form.Control
                type='text'
                className='bg-light border-secondary'
                placeholder='Search Bookings'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (isClientSide) setClientPage(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
              {!isClientSide && (
                <Button
                  variant='outline-secondary'
                  onClick={handleSearch}
                  title='Search'
                >
                  <AiOutlineSearch />
                </Button>
              )}
            </InputGroup>

            {hasPermission('bookings:book') && (
              <button
                className='btn btn-primary h-100 d-flex align-items-center text-nowrap'
                title='Book Test'
                onClick={() => navigate('/book-test')}
              >
                <FaPlus />
                <span className='d-none d-md-inline ms-2'>Book Test</span>
              </button>
            )}
          </div>
        </div>

        {status === 'loading' ? (
          <div className="d-flex justify-content-center py-5">
            <h4 className='text-muted'>Loading bookings...</h4>
          </div>
        ) : (
          <>
            <BookingTable
              bookings={tableData}
              startIndex={startIndex}
              onUpdateClick={handleUpdateClick}
              isClientSide={isClientSide}
            />

            {isClientSide ? (
              <PartnerPagination
                currentPage={clientPage}
                totalPages={totalClientPages}
                onPageChange={setClientPage}
                onItemsPerPageChange={(newLimit) => {
                  setItemsPerPage(newLimit);
                  setClientPage(1);
                }}
              />
            ) : (
              <SupportPagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                fetchData={fetchMyBookings}
                searchKeyword={searchTerm}
              />
            )}
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default Bookings;